import React, { useMemo, useState } from "react";
import {
  Ban,
  Crown,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  UserCheck,
} from "lucide-react";

import Topbar from "../components/Topbar";
import { EmptyState, ErrorState, TableSkeleton } from "../components/ui/Feedback";
import { Avatar, FilterTabs, Pagination } from "../components/ui/Table";
import { useQuery } from "../lib/useQuery";
import { fetchAuditLog, type AuditEntry } from "../lib/api";

/**
 * Audit log.
 *
 * Every privileged action taken through this panel writes a row to
 * `admin_actions` (migration 0008), and until now nothing read them back.
 *
 * The reason this page exists isn't bookkeeping. There are two admin accounts
 * on this database and each can suspend users, comp Premium, delete anyone's
 * posts, and promote further admins. If one of those sessions is ever phished,
 * this page is the only place that will tell you what was done with it — and
 * the rows can't be forged or erased through the API, because `log_admin_action`
 * is security definer and its EXECUTE grant is revoked from `authenticated`.
 */

const PAGE_SIZE = 50;

const ACTIONS: { key: string; label: string }[] = [
  { key: "all", label: "Everything" },
  { key: "suspend_user", label: "Suspensions" },
  { key: "grant_premium", label: "Premium granted" },
  { key: "delete_post", label: "Posts deleted" },
  { key: "delete_comment", label: "Comments deleted" },
  { key: "set_order_status", label: "Order status" },
  { key: "grant_admin", label: "Admin granted" },
];

const ICONS: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  suspend_user: { icon: Ban, color: "var(--danger)", bg: "var(--danger-light)" },
  unsuspend_user: { icon: UserCheck, color: "var(--success)", bg: "var(--success-light)" },
  grant_premium: { icon: Crown, color: "#b97800", bg: "var(--warning-light)" },
  revoke_premium: { icon: Crown, color: "var(--text-muted)", bg: "#f2f4f8" },
  delete_post: { icon: Trash2, color: "var(--danger)", bg: "var(--danger-light)" },
  delete_comment: { icon: Trash2, color: "var(--danger)", bg: "var(--danger-light)" },
  set_order_status: { icon: ShoppingCart, color: "var(--primary)", bg: "var(--primary-light)" },
  grant_admin: { icon: ShieldCheck, color: "var(--primary)", bg: "var(--primary-light)" },
  revoke_admin: { icon: ShieldCheck, color: "var(--danger)", bg: "var(--danger-light)" },
};

const dateTime = (value: string) =>
  new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Turns a detail blob into the one sentence that matters for each verb. */
function describe(entry: AuditEntry): string | null {
  const detail = entry.detail ?? {};
  switch (entry.action) {
    case "suspend_user":
      return detail.reason ? `Reason: ${detail.reason}` : "No reason recorded";
    case "grant_premium":
    case "revoke_premium":
      return detail.note ? `Note: ${detail.note}` : null;
    case "set_order_status":
      return `${detail.from} → ${detail.to}`;
    case "delete_post":
    case "delete_comment": {
      const parts: string[] = [];
      if (detail.reason) parts.push(`Reason: ${detail.reason}`);
      if (detail.excerpt) parts.push(`“${String(detail.excerpt).slice(0, 120)}”`);
      return parts.join(" · ") || null;
    }
    default:
      return null;
  }
}

export default function AuditLog() {
  const [action, setAction] = useState("all");
  const [offset, setOffset] = useState(0);

  const log = useQuery(
    () => fetchAuditLog({ action, limit: PAGE_SIZE, offset }),
    [action, offset]
  );

  const rows = useMemo(() => log.data?.rows ?? [], [log.data]);

  return (
    <div>
      <Topbar
        title="Audit log"
        subtitle="Every privileged action taken through this panel"
      />
      <div className="page-shell">
        <div style={{ marginBottom: 18 }}>
          <FilterTabs
            tabs={ACTIONS}
            active={action}
            onChange={(next) => {
              setAction(next);
              setOffset(0);
            }}
          />
        </div>

        <div className="card">
          {log.error ? (
            <ErrorState error={log.error} onRetry={log.refetch} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 46 }} />
                      <th>Action</th>
                      <th>By</th>
                      <th>Target</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.loading && <TableSkeleton rows={8} cols={5} />}

                    {!log.loading &&
                      rows.map((entry) => {
                        const style = ICONS[entry.action] ?? {
                          icon: ShieldCheck,
                          color: "var(--text-muted)",
                          bg: "#f2f4f8",
                        };
                        const Icon = style.icon;
                        const note = describe(entry);

                        return (
                          <tr key={entry.id}>
                            <td>
                              <span
                                style={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 9,
                                  background: style.bg,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Icon size={14} color={style.color} />
                              </span>
                            </td>
                            <td>
                              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                                {entry.action.replace(/_/g, " ")}
                              </div>
                              {note && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                    marginTop: 2,
                                    maxWidth: 460,
                                  }}
                                >
                                  {note}
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Avatar
                                  name={entry.profiles?.full_name ?? null}
                                  seed={entry.admin_id}
                                  size={26}
                                />
                                <span style={{ fontWeight: 650 }}>
                                  {entry.profiles?.full_name ?? "Unknown"}
                                </span>
                              </div>
                            </td>
                            <td
                              style={{
                                fontFamily: "ui-monospace, Menlo, monospace",
                                fontSize: 11.5,
                                color: "var(--text-muted)",
                              }}
                            >
                              {entry.target_table}
                              {entry.target_id && (
                                <div>{entry.target_id.slice(0, 8)}…</div>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>{dateTime(entry.created_at)}</td>
                          </tr>
                        );
                      })}

                    {!log.loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0 }}>
                          <EmptyState
                            message="Nothing recorded yet"
                            hint="Suspensions, Premium changes, deletions and order updates all appear here."
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={log.data?.total ?? 0}
                onChange={setOffset}
                busy={log.fetching}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
